import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../../services/api';
import { CHYPS_QUESTIONS, CHYPS_OPTIONS } from './chypsQuestions';

const ALREADY_SUBMITTED_MESSAGE =
  'Already submitted. Please ask your clinician if you want to re-fill the questionnaire.';

const DEBUG = process.env.REACT_APP_VERBOSE === 'true';

const ChypsQuestionnaire: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  // Unconditional log so we can see in console if this route is rendering at all
  console.log('[CHYPS] Page mounted', { token: token ?? '(none)', path: window.location.pathname });
  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [title, setTitle] = useState<string>('Symptoms Profile');
  const [answers, setAnswers] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  if (DEBUG) {
    console.log('[CHYPS] ChypsQuestionnaire render', { token, loading, invalid, alreadySubmitted, submitSuccess });
  }

  useEffect(() => {
    if (DEBUG) console.log('[CHYPS] useEffect run', { token });
    if (!token) {
      if (DEBUG) console.log('[CHYPS] No token, set invalid');
      setInvalid(true);
      setLoading(false);
      return;
    }
    if (DEBUG) console.log('[CHYPS] Fetching by-token...', token.slice(0, 8) + '...');
    api
      .chypsByToken(token)
      .then((data) => {
        if (DEBUG) console.log('[CHYPS] by-token response', data);
        if (!data.valid) {
          setInvalid(true);
          setErrorMessage(data.error || 'This link is invalid or has expired. It may have been removed by your clinician.');
          return;
        }
        setAlreadySubmitted(!!data.already_submitted);
        if (data.title) setTitle(data.title);
        const initial: Record<string, number> = {};
        CHYPS_QUESTIONS.forEach((q) => (initial[q.id] = 0));
        setAnswers(initial);
      })
      .catch((e: { data?: { error?: string }; message?: string }) => {
        if (DEBUG) console.log('[CHYPS] by-token error', e);
        setInvalid(true);
        setErrorMessage(e?.data?.error || e?.message || 'This link is invalid or has expired. It may have been removed by your clinician.');
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handleChange = (questionId: string, value: number) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || submitting) return;
    setSubmitting(true);
    api
      .chypsSubmitByToken(token, answers as unknown as Record<string, unknown>)
      .then(() => setSubmitSuccess(true))
      .catch((e: { data?: { error?: string }; message?: string }) => {
        setErrorMessage(e?.data?.error || e?.message || 'Submit failed.');
      })
      .finally(() => setSubmitting(false));
  };

  const pageStyle: React.CSSProperties = { minHeight: '100vh', padding: 48 };

  if (loading) {
    return (
      <div style={{ ...pageStyle, textAlign: 'center' }}>
        <p>Loading…</p>
      </div>
    );
  }

  if (invalid) {
    return (
      <div style={{ ...pageStyle, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <h1>Invalid or expired link</h1>
        <p style={{ color: '#666', marginTop: 16 }}>{errorMessage || 'This link is not valid or has expired.'}</p>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div style={{ ...pageStyle, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <h1>{title}</h1>
        <p style={{ fontSize: 18, marginTop: 24 }}>{ALREADY_SUBMITTED_MESSAGE}</p>
      </div>
    );
  }

  if (submitSuccess) {
    return (
      <div style={{ ...pageStyle, textAlign: 'center', maxWidth: 560, margin: '0 auto' }}>
        <h1>Thank you</h1>
        <p style={{ fontSize: 18, marginTop: 24 }}>Your responses have been submitted successfully.</p>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, padding: 24, maxWidth: 720, margin: '0 auto' }}>
      <h1>{title}</h1>
      <p style={{ color: '#666', marginBottom: 24 }}>
        Please answer each question by choosing how often you experience the following. Your clinician will receive your responses.
      </p>
      <form onSubmit={handleSubmit}>
        {CHYPS_QUESTIONS.map((q, index) => (
          <div key={q.id} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: '1px solid #eee' }}>
            <label style={{ display: 'block', fontWeight: 500, marginBottom: 8, fontSize: 15 }}>
              {index + 1}. {q.label}
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
              {CHYPS_OPTIONS.map((opt) => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name={q.id}
                    value={opt.value}
                    checked={(answers[q.id] ?? 0) === opt.value}
                    onChange={() => handleChange(q.id, opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        ))}
        {errorMessage && <p style={{ color: '#c00', marginBottom: 16 }}>{errorMessage}</p>}
        <button type="submit" disabled={submitting} style={{ padding: '12px 24px', marginTop: 16, fontSize: 16 }}>
          {submitting ? 'Submitting…' : 'Submit'}
        </button>
      </form>
    </div>
  );
};

export default ChypsQuestionnaire;
